import { DateEditorHelper } from './datepicker-helper'
import { dropdownNumericRenderer } from './dropdown-helper'
import { isNumeric, getInnerText, fastInnerText } from './renderer-helper'

declare var Handsontable
declare var numbro

export const CHANGED_CELL_COLOR = {
  key: 'changedCellColor',
  value: '#FFC000'
}

export const BODER_STYLE_DEFS:string[] = ['1px solid', '1px dashed', '1px dotted', '1.5px solid']

export const createRenderer = (options, cellHeight): {cells: (row:number, col:number, props) => {}, headerRenderer:(col:number, th) => void} => {
  const palette:string[] = options.colorPalette
  const formatDefs:string[] = options.formatDefs.map(r => {
    if(String(r).includes('[') && String(r).includes(']')){
      return String(r).replace(/\[.+\]/ , '[red]')
    }else{
     return r
    }
  })
  const allowedValues:string[][] = options.constraintDefs
  const bgColors:(number | string)[][] = options.bgColor && options.bgColor.map(r => r || [])
  const fgColors:number[][] = options.fgColor && options.fgColor.map(r => r || [])
  const bdStyles:string[][] = options.bdStyle && options.bdStyle.map(r => r || [])
  const bdColors:string[][] = options.bdColor && options.bdColor.map(r => r || [])
  const lockState:number[][] = options.locked.map(r => r || [])
  const formats:number[][] = options.format.map(r => r || [])
  const constraints:number[][] = options.constraint.map(r => r || [])
  const bindingPaths:string[][] = options.bindingPaths.map(r => r || [])
  const hiddenCols:number[] = options.hiddenCols
  const hiddenRows:number[] = options.hiddenRows
  const headerStartRowNum:number = options.headerStartRowNum
  const headerEndRowNum:number = options.headerEndRowNum
  const startRowNum:number = options.startRowNum
  const endRowNum:number = options.endRowNum
  const startColNum = options.startColNum
  const endColNum = options.endColNum
  const formatColor:{row:number, col:number}[] = []
  const filterSettings:number[] = options.filterSettings
  const sortSettings:{column:number, sortOrder:string}[] = options.sortSettings
  const mergeHeaders:{row:number ,col:number, rowspan:number, colspan:number}[] = options.mergeHeaders
  const columnSettings:{index:number, showSelectAll:boolean, alignment:string}[] = options.columnSettings || []
  const iconCells:string[] = options.iconCells && options.iconCells.map(r => r || [])
  const HEADER_CLASS_FILTER_BUTTON = 'changeType'
  const HEADER_CLASS_RELATIVE_DIV = 'relative'
  const HEADER_CLASS_COL_HEADER = 'colHeader'
  const HEADER_CLASS_HIDDEN_HEADER = 'hiddenHeader'
  const HEADER_CLASS_SORTING = 'columnSorting'
  const HEADER_CLASS_ASC_SORT = 'ascending';
  const HEADER_CLASS_DESC_SORT = 'descending';

  const rowOffset = (headerEndRowNum || headerEndRowNum === 0) ? headerEndRowNum + 1 : 0

  const isHiddenRow = (row:number):boolean => {
    return hiddenRows.indexOf(row) > -1
  }

  const isHiddenCol = (col:number):boolean => {
    return startColNum > col
        || endColNum < col
        || hiddenCols.indexOf(col) > -1
  }

  const bdStyleOf = (code:number):string => {
    if (code == null) return null
    return BODER_STYLE_DEFS[code]
  }

  const colorOf = (code:number):string => {
    if (code == null) return null
    return palette[code]
  }

  const allowedValuesAt = (row:number, col:number):string[] => {
    const index = constraints[row][col]
    if (index == null) return null
    return allowedValues[index]
  }

  const shownIconAt = (row:number, col:number):string => {
    const icon = iconCells.find(c => c.indexOf(row + ',' + col + '|') === 0)
    if (icon == null) return null
    return icon.split('|')[1]
  }

  const alignmentAt = (col:number):string => {
    const settings = columnSettings.find(c => c.index === col && !!c.alignment)
    if (settings == null || !settings.alignment) return null
    switch (settings.alignment) {
      case 'center': return 'htCenter'
      case 'right': return 'htRight'
      case 'left': return 'htLeft'
    }
    return null
  }

  const isShownAsCheckbox = (allowedValues:string[]):boolean => {
    return allowedValues
        && (allowedValues.length === 2)
        && (allowedValues.indexOf('Y') > -1)
        && (allowedValues.indexOf('N') > -1)
  }
  
  const changeUserDefinedFormat = (pattern:string ,value:number):number => {
    if(!value && value !== 0)return
    var returnVal = value;
    if(/"([^"]*%[^"]*)+"/.test(pattern)){
      // %で表示の場合
      returnVal = returnVal / 100
    }
    if (/,;/.test(pattern) || /,$/.test(pattern)) {
      // 末尾に,がつくフォーマットの場合、1000で割る　例："#,###,;-#,###,;
      returnVal = returnVal / 1000
    }
    if (!/\./.test(pattern)) {
      // .を含まない場合、小数を表示しない　例：0,0
      returnVal = Math.round(returnVal)
    }
    //NaNになる場合は元の値を返す
    return Number.isNaN(returnVal)? value :returnVal
  }

  const formatOf = (row:number, col:number):{[key:string]:any} => {
    const code = formats[row][col]
    if (code == null) return null
    var format = formatDefs[code]
    if (format.includes('[red]')) {
      formatColor.push({row:row, col:col})
      format = format.replace('[red]','')
    }
    const allowedValues = allowedValuesAt(row, col)
    if (isShownAsCheckbox(allowedValues)) {
      return {
        type:'dropdown',
        checkedTemplate: 'Y',
        uncheckedTemplate: 'N',
      }
    }
    if (allowedValues) {
      if (!format || !/[#0-9]/.test(format)) {
        return {
          type: 'dropdown',
          source: allowedValues,
        }
      } else {
        return {
          type: 'dropdown',
          source: allowedValues,
          numericFormat: {
            pattern: format,
          },
        }
      }
    }
    const icon = shownIconAt(row, col);
    if (icon) {
      return {
        type: 'icon',
        iconType: icon,
      }
    }
    if (!format) return {}
    if (format.includes('y') || format.includes('m')) {
      return {
        type: 'date',
        dateFormat: format.replace(/[@;]+/, '').toUpperCase(),
        correctFormat: true,
        editor: DateEditorHelper,
      }
    }
    if (/[#0-9]/.test(format)) {
      return {
        type: 'numeric',
        numericFormat: {
          pattern: format,
        },
      }
    }
    return {}
  }
  
  const formatHeader = (row:number, col:number, header:any):void => {
    const code = formats[row][col]
    if (code == null) return
    var format = formatDefs[code]
    if (!format) return
    var value:any = getInnerText(header);
    if(!value && value !== 0)return
    value = changeUserDefinedFormat(format, value)
    if (/[#0-9]/.test(format) && isNumeric(value)) {
      fastInnerText(header, numbro(value).format(format || '0'))
      if (!header.classList.contains('htRight')) header.classList.add('htRight')
    }
  }

  function cellRenderer(instance, td, _row, _col, prop, value, cellProperties):void {
    const hiddenColumnsPlugin = instance.getPlugin('hiddenColumns');
    const hiddenRowsPlugin = instance.getPlugin('hiddenRows');
    const row = cellProperties.row + rowOffset
    const col = cellProperties.col
    const format:string = cellProperties.type
    let isHiddenCell = false
    let attribute = ""
    let tr = td.parentElement
    if (isHiddenCol(cellProperties.col)) {
      hiddenColumnsPlugin.hideColumn(col);
      isHiddenCell = true
    }
    if (isHiddenRow(cellProperties.row)) {
      hiddenRowsPlugin.hideRow(_row);
      isHiddenCell = true
    } else if (!hiddenRowsPlugin.isHidden(_row)) {
      hiddenRowsPlugin.showRow(_row)
    }
    switch (format) {
      case 'dropdown':
        (cellProperties.checkedTemplate)
        ? Handsontable.renderers.CheckboxRenderer.apply(this, [
            instance, td, _row, _col, prop, (value === 'Y' ? 'Y' : 'N'), cellProperties
          ])
        : Handsontable.renderers.AutocompleteRenderer.apply(this, arguments)
        if (cellProperties.numericFormat && cellProperties.numericFormat.pattern) {
          dropdownNumericRenderer.apply(this, [
              instance, td, _row, _col, prop, changeUserDefinedFormat(cellProperties.numericFormat.pattern, value), cellProperties
            ])
          attribute += 'padding-right: 20px;'
        }
        break
      case 'numeric':
        (cellProperties.numericFormat && cellProperties.numericFormat.pattern)
        ? Handsontable.renderers.NumericRenderer.apply(this, [
            instance, td, _row, _col, prop, changeUserDefinedFormat(cellProperties.numericFormat.pattern, value),
              cellProperties
          ])
        : Handsontable.renderers.NumericRenderer.apply(this, arguments)
        break
      case 'date':
        cellProperties.defaultDate = value
        Handsontable.renderers.DateRenderer.apply(this, arguments)
        break
      case 'icon':
        iconRenderer.apply(this, arguments)
        break
      default:
        Handsontable.renderers.TextRenderer.apply(this, arguments)
    }

    if (isHiddenCell) {
      return
    }
    if (!options.wrapText) {
      // td.style.whiteSpace = 'nowrap'
      attribute += 'white-space: nowrap;'
      td.setAttribute('data-tooltip', value);
    }
    attribute += createCellStyles(instance, row, col, value, false)
    td.setAttribute('style', attribute);
    const alignment = alignmentAt(col)
    if (!!alignment) td.classList.add(alignment)
  }

  function headerRenderer(_col, th):void {
    let tr = th.parentNode
    let _row = tr.rowIndex
    let groupingHeaderCount = !!this.getSettings().groupingHeaderCount ? this.getSettings().groupingHeaderCount : 0
    if (_row < groupingHeaderCount) {
      th.classList.add('col_grouping')
      if (th.querySelector('div.collapsibleIndicator.expanded')) th.classList.add('expanded')
      return;
    }
    if (headerStartRowNum == undefined || headerStartRowNum == null) return
    const row = _row + headerStartRowNum - groupingHeaderCount
    const col = _col
    if (col === false || col < 0) return
    let attribute = createCellStyles(this, row, col, null, true)

    // Process filter and sorting
    let filterButton = th.querySelector('.' + HEADER_CLASS_FILTER_BUTTON)
    let colHeaderSpan = th.querySelector('.' + HEADER_CLASS_COL_HEADER)
    // filtering
    if (filterSettings && filterSettings.indexOf(col) === -1) {
      if (filterButton) {
        filterButton.parentNode.removeChild(filterButton);
      }
    }
    // sorting
    if (!!sortSettings && sortSettings.findIndex(x => x.column === col) === -1) {
      if (colHeaderSpan && colHeaderSpan.classList.contains(HEADER_CLASS_SORTING)) {
        colHeaderSpan.classList.remove(HEADER_CLASS_SORTING)
        colHeaderSpan.classList.remove(HEADER_CLASS_ASC_SORT)
        colHeaderSpan.classList.remove(HEADER_CLASS_DESC_SORT)
      }
    }
    // Format cell value
    formatHeader(row, col, colHeaderSpan)

    let mergeHeader = mergeHeaders.find(h => h.row == row && h.col == col)
    let rowspan = mergeHeader && mergeHeader.rowspan > 1 ? mergeHeader.rowspan : 1
    if (rowspan > 1) {
      th.setAttribute('rowspan', rowspan)
    } else {
      th.removeAttribute('rowspan')
      let mergeRowHeader = mergeHeaders.find(h => h.row < row && h.row + h.rowspan > row && h.col <= col && h.col + h.colspan > col);
      if (!th.classList.contains(HEADER_CLASS_HIDDEN_HEADER) && mergeRowHeader) {
        th.classList.add(HEADER_CLASS_HIDDEN_HEADER)
        attribute += 'display: none;'
        if (tr.nextElementSibling === null) {
          let filterButton = th.querySelector('.' + HEADER_CLASS_FILTER_BUTTON)
          let colHeaderSpan = th.querySelector('.' + HEADER_CLASS_COL_HEADER)
          let headerRow = mergeRowHeader.row - headerStartRowNum + groupingHeaderCount;
          let headerCol = th.cellIndex - col + mergeRowHeader.col;
          if (tr.parentNode.childNodes.length > headerRow
              && tr.parentNode.childNodes[headerRow].childNodes.length > headerCol) {
            let mergedTh = tr.parentNode.childNodes[headerRow].childNodes[headerCol];
            // filtering
            if (filterButton) {
              mergedTh.querySelector('div.' + HEADER_CLASS_RELATIVE_DIV).appendChild(filterButton);
            }
            // sorting
            let mergedColHeaderSpan = mergedTh.querySelector('.' + HEADER_CLASS_COL_HEADER)
            if (mergedColHeaderSpan && colHeaderSpan.classList.contains(HEADER_CLASS_SORTING)) {
              mergedColHeaderSpan.classList.add(HEADER_CLASS_SORTING)
              if (colHeaderSpan.classList.contains(HEADER_CLASS_ASC_SORT)) {
                mergedColHeaderSpan.classList.add(HEADER_CLASS_ASC_SORT)
              }
              if (colHeaderSpan.classList.contains(HEADER_CLASS_DESC_SORT)) {
                mergedColHeaderSpan.classList.add(HEADER_CLASS_DESC_SORT)
              }
            }
          }
        }
      }
    }
    // Select All / Cancel All
    if (columnSettings.length > 0) {
      if (!th.classList.contains(HEADER_CLASS_HIDDEN_HEADER) && (_row + rowspan === tr.parentNode.childNodes.length)) {
        // Keep ALL/CLEAR status
        const _checkboxColumns = this.getSettings()._checkboxColumns || []
        if (_checkboxColumns.length === 0) {
          columnSettings.forEach(x => _checkboxColumns.push({index: x.index, showAllBtn: true}))
          this.getSettings()._checkboxColumns = _checkboxColumns
        }
        const columnSetting = columnSettings.find(x => x.index === col && x.showSelectAll === true)
        if (columnSetting) {
          let btnStatus = _checkboxColumns.find(x => x.index === col)
          if (this.getDataAtCol(col).find(d => d === 'Y')) {
            btnStatus.showAllBtn = false;
          } else {
            btnStatus.showAllBtn = true;
          }
          const btnArea = document.createElement('div')
          btnArea.className = 'btnArea'
          th.querySelector('div.' + HEADER_CLASS_RELATIVE_DIV).appendChild(btnArea)
          const button = document.createElement('input')
          button.type = 'button'
          button.value = !!btnStatus.showAllBtn ? 'ALL' : 'CLEAR'
          button.addEventListener('click', e => {
            btnStatus.showAllBtn = !btnStatus.showAllBtn
            const countRows = this.countRows()
            if (countRows > 0) {
              this.populateFromArray(0, col, [[btnStatus.showAllBtn ? 'N' : 'Y']], countRows - 1, col, 'Autofill.fill', 'overwrite', 'down')
            }
            e.stopPropagation()
          })
          btnArea.appendChild(button)
        }
      }
    }
    th.setAttribute('style', attribute);
  }

  function createCellStyles(instance, _row, _col, value, isHeaderRenderer):string {
    const closedColumnGroups:{row:number, fromCol:number, toCol:number}[] = instance.getSettings().closedColumnGroups;
    const row = _row
    const col = _col
    let attribute = ""
    if (bgColors) {
      const bgColor = bgColors[row][col]
      if (bgColor != null) {
        attribute += `background: ${colorOf(bgColor as number)};`
      } else if (isHeaderRenderer) {
        attribute += `background: #FFFFFF;`
      }
    }
    if(formatColor.filter(obj => obj.row === row && obj.col === col).length > 0 && value-0 < 0){
      attribute += `color: #ff0000;`
    }else if (fgColors){
      const fgColor = fgColors[row][col]
      if (fgColor != null) attribute += `color: ${colorOf(fgColor)};`
    }
    if (bdStyles && bdColors) {
      let defaultBdColor = '#ffffff'
      if (bgColors && bgColors[row][col] != null) {
        defaultBdColor = colorOf(bgColors[row][col] as number)
      }
      const bdStyle:string[] = bdStyles[row][col] && bdStyles[row][col].split('|')
      const bdColor:string[] = bdColors[row][col] && bdColors[row][col].split('|')
      if (bdStyle != null && bdColor != null) {
        if (bdStyle[0] !== 'null' && bdColor[0] !== 'null') {
          attribute += `border-top: ${bdStyleOf(Number(bdStyle[0])) + colorOf(Number(bdColor[0]))};`
        } else {
          attribute += `border-top: 1px solid ${defaultBdColor};`
        }
        if (bdStyle[1] !== 'null' && bdColor[1] !== 'null') {
          attribute += `border-right: ${bdStyleOf(Number(bdStyle[1])) + colorOf(Number(bdColor[1]))};`
        } else {
          attribute += `border-right-width: 0px;`
        }
        if (bdStyle[2] !== 'null' && bdColor[2] !== 'null') {
          attribute += `border-bottom: ${bdStyleOf(Number(bdStyle[2])) + colorOf(Number(bdColor[2]))};`
        } else {
          attribute += `border-bottom-width: 0px;`
        }
        if (bdStyle[3] !== 'null' && bdColor[3] !== 'null') {
          attribute += `border-left: ${bdStyleOf(Number(bdStyle[3])) + colorOf(Number(bdColor[3]))};`
        } else {
          let targetClosedGroups = closedColumnGroups.filter(x => x.toCol === col);
          let targetClosedGroup = null
          if (targetClosedGroups.length > 0) targetClosedGroup = targetClosedGroups.reduce((x, y) => {return x.fromCol < y.fromCol ? x : y})
          const bdStyleGroup:string[] = targetClosedGroup ? bdStyles[row][targetClosedGroup.fromCol] && bdStyles[row][targetClosedGroup.fromCol].split('|') : null
          const bdColorGroup:string[] = targetClosedGroup ? bdColors[row][targetClosedGroup.fromCol] && bdColors[row][targetClosedGroup.fromCol].split('|') : null
          if (bdStyleGroup && bdColorGroup && bdStyleGroup[3] !== 'null' && bdColorGroup[3] !== 'null') {
            attribute += `border-left: ${bdStyleOf(Number(bdStyleGroup[3])) + colorOf(Number(bdColorGroup[3]))};`
          } else {
            attribute += `border-left: 1px solid ${defaultBdColor};`
          }
        }
      } else {
        attribute += `border-right-color: ${defaultBdColor};`
        attribute += `border-bottom-color: ${defaultBdColor};`
      }
    }
    return attribute
  }

  function emptyCellRenderer(instance, td, _row, _col, prop, value, cellProperties):void {
    td.classList.add('emptyCell');
    Handsontable.renderers.TextRenderer.apply(this, arguments);
  }

  function iconRenderer(instance, td, _row, _col, prop, value, cellProperties) {
    const html = `<ion-icon name="${cellProperties.iconType}" class="icon icon-md ion-md-${cellProperties.iconType}"></ion-icon>`
    Handsontable.renderers.HtmlRenderer.apply(this, [instance, td, _row, _col, prop, html, cellProperties])
  }

  return {
    cells: (_row:number, _col:number, props) => {
      const row = _row + rowOffset
      const col = _col
      if (row > endRowNum || col > endColNum) {
        return {
          readOnly: true,
          renderer: emptyCellRenderer
        }
      }
      const format = formatOf(row, col)
      return {
        renderer: cellRenderer,
        readOnly: !lockState || lockState[row][col] !== 0,
        ...format,
      }
    },
    headerRenderer: headerRenderer
  };
}



// WEBPACK FOOTER //
// ./src/widgets/spread_sheet/spread-sheet/plugins/cell_renderer.ts